import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { getBookingCalendar } from '../../api/bookingCalendarApi';
import './BookingCalendar.scss';

const TAB_GROUP = 'group';
const TAB_PRIVATE = 'private';

function formatDate(iso) {
  if (!iso) return '—';
  return moment(iso).format('D MMM YYYY');
}

function statusBadgeClass(status) {
  if (status === 'paid') return 'success';
  if (status === 'waiting') return 'warning';
  if (status === 'cancelled') return 'secondary';
  return 'light';
}

function MonthSummary({ label, stats }) {
  const total = stats?.total || 0;
  const paid = stats?.paid || 0;
  const waiting = stats?.waiting || 0;

  return (
    <div className="booking-cal__month-line">
      <span className="booking-cal__month-line-label">{label}</span>
      <span className="booking-cal__month-line-total">{total}</span>
      {total > 0 && (
        <span className="booking-cal__month-line-detail">
          {paid} paid{waiting > 0 ? ` · ${waiting} waiting` : ''}
        </span>
      )}
    </div>
  );
}

export default function BookingCalendar() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_GROUP);
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getBookingCalendar({ year });
      setSummary(res.data);
    } catch (e) {
      console.warn('BookingCalendar summary error', e);
      setError(true);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [year]);

  const fetchDetail = useCallback(async (month) => {
    if (!month) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    try {
      const res = await getBookingCalendar({ year, month });
      setDetail(res.data);
    } catch (e) {
      console.warn('BookingCalendar detail error', e);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchSummary();
    setSelectedMonth(null);
    setDetail(null);
  }, [fetchSummary]);

  useEffect(() => {
    fetchDetail(selectedMonth);
  }, [selectedMonth, fetchDetail]);

  const handleMonthClick = (month) => {
    setSelectedMonth((prev) => (prev === month ? null : month));
  };

  const selectedMonthMeta = summary?.months?.find((m) => m.month === selectedMonth);
  const groupItems = detail?.group_items || [];
  const privateItems = detail?.private_items || [];

  return (
    <div className="container py-4 booking-cal">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-1">Booking Calendar</h2>
          <p className="text-muted mb-0 small">
            Click a month to see every booking. Counts always match the list below.
          </p>
        </div>
        <div className="booking-cal__year-nav">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">‹</button>
          <span className="booking-cal__year-label">{year}</span>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setYear((y) => y + 1)} aria-label="Next year">›</button>
        </div>
      </div>

      <div className="booking-cal__legend mb-3">
        <span><span className="badge badge-success">Paid</span> money received</span>
        <span><span className="badge badge-warning">Waiting</span> needs deposit or payment</span>
        <span><span className="badge badge-secondary">Cancelled</span> not running</span>
      </div>

      {error && (
        <div className="alert alert-warning">
          Could not load calendar. <button type="button" className="btn btn-sm btn-outline-secondary" onClick={fetchSummary}>Retry</button>
        </div>
      )}

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="sr-only">Loading…</span></div>
        </div>
      )}

      {!loading && !error && summary && (
        <div className="booking-cal__grid" role="list">
          {summary.months.map((m) => {
            const isSelected = selectedMonth === m.month;
            const isCurrentMonth = year === currentYear && m.month === new Date().getMonth() + 1;
            const needsAttention = (m.group?.waiting || 0) + (m.private?.waiting || 0);

            return (
              <button
                key={m.year_month}
                type="button"
                role="listitem"
                className={`booking-cal__cell${isSelected ? ' booking-cal__cell--selected' : ''}${isCurrentMonth ? ' booking-cal__cell--current' : ''}`}
                onClick={() => handleMonthClick(m.month)}
                aria-pressed={isSelected}
              >
                <span className="booking-cal__cell-label">{m.label}</span>
                <MonthSummary label="Group" stats={m.group} />
                <MonthSummary label="Private" stats={m.private} />
                {needsAttention > 0 && (
                  <span className="booking-cal__cell-attention">{needsAttention} waiting</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {selectedMonth && selectedMonthMeta && (
        <section className="booking-cal__detail mt-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <h3 className="h5 mb-0">
              {moment(`${year}-${String(selectedMonth).padStart(2, '0')}-01`).format('MMMM YYYY')}
            </h3>
            <ul className="nav nav-pills booking-cal__tabs">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link${activeTab === TAB_GROUP ? ' active' : ''}`}
                  onClick={() => setActiveTab(TAB_GROUP)}
                >
                  Group ({groupItems.length})
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link${activeTab === TAB_PRIVATE ? ' active' : ''}`}
                  onClick={() => setActiveTab(TAB_PRIVATE)}
                >
                  Private ({privateItems.length})
                </button>
              </li>
            </ul>
          </div>

          {detailLoading && (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" role="status"><span className="sr-only">Loading…</span></div>
            </div>
          )}

          {!detailLoading && activeTab === TAB_GROUP && (
            groupItems.length === 0 ? (
              <p className="text-muted mb-0">No group bookings this month.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover booking-cal__table">
                  <thead>
                    <tr>
                      <th>Trip</th>
                      <th>Start date</th>
                      <th>People</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {groupItems.map((item) => (
                      <tr key={item.idx}>
                        <td>
                          <strong>{item.name}</strong>
                          {item.journey_slug && <div className="text-muted small">{item.journey_slug}</div>}
                        </td>
                        <td>{formatDate(item.start_date)}</td>
                        <td>{item.paid_people_count} / min {item.min_group_size}</td>
                        <td><span className={`badge badge-${statusBadgeClass(item.status)}`}>{item.status_label}</span></td>
                        <td className="text-right">
                          <Link to={`/admin/group_tour_details/${item.idx}`} className="btn btn-sm btn-primary">Manage</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {!detailLoading && activeTab === TAB_PRIVATE && (
            privateItems.length === 0 ? (
              <p className="text-muted mb-0">No private bookings this month.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover booking-cal__table">
                  <thead>
                    <tr>
                      <th>Customer / journey</th>
                      <th>Travel date</th>
                      <th>Pax</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {privateItems.map((item) => (
                      <tr key={item.idx}>
                        <td>
                          <strong>{item.journey_title || item.journey_slug || 'Private tour'}</strong>
                          <div className="text-muted small">{item.head_traveller_name || '—'}</div>
                        </td>
                        <td>{formatDate(item.travel_date)}</td>
                        <td>{item.pax}</td>
                        <td><span className={`badge badge-${statusBadgeClass(item.status)}`}>{item.status_label}</span></td>
                        <td className="text-right">
                          <Link to={`/admin/inquiry_details/${item.idx}`} className="btn btn-sm btn-primary">Manage</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </section>
      )}

      {!selectedMonth && !loading && !error && (
        <p className="text-muted text-center mt-4 mb-0">Click a month to open the list and manage bookings.</p>
      )}
    </div>
  );
}
